/**
 * Per-kind constants for the 6 wiki callout panels — info / note /
 * success / warning / error / customPanel.
 *
 * Keeps every callsite (extension factory, NodeView, slash-command
 * registry, tests, nesting-guard helper) reading from a single source
 * of truth so a refactor that adds a 7th kind in a future phase doesn't
 * require an N-file sweep. The 5 named kinds share a uniform shape;
 * `customPanel` adds two attribute axes (color + icon) but otherwise
 * routes through the same factory.
 *
 * Theme contract (UX Agent 2 + ADR-016):
 *   - light: stripe `bg-{shade}-500/600`, icon `text-{shade}-500/600`.
 *   - dark : stripe `dark:bg-{shade}-300`, icon `dark:text-{shade}-300`.
 *   `warning` uses `amber-600` on light (amber-500 fails 3:1 on white).
 *
 * Lucide icon names are imported once, here, so the icon set is the
 * same one referenced by the slash-menu registry. Changing an icon
 * here updates every render path automatically.
 */

import {
  AlertOctagon,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Flag,
  Info,
  Lightbulb,
  Lock,
  type LucideIcon,
  MessageSquare,
  OctagonAlert,
  Pin,
  Star,
  TriangleAlert,
  Wrench,
} from 'lucide-react'

/**
 * Canonical kind discriminator. The 5 named kinds map 1:1 to ProseMirror
 * node names (`infoPanel` etc.); `customPanel` is the only kind that
 * carries `color` + `icon` attributes.
 */
export type PanelKind = 'info' | 'note' | 'success' | 'warning' | 'error' | 'customPanel'

/**
 * Set of every ProseMirror node name the 6 panels register. Used by the
 * slash-command `run()` to short-circuit a panel-in-panel insert
 * silently — `editor.isActive(name)` against this set is the cheapest
 * guard we have (no need to walk the resolved position).
 */
export const PANEL_NODE_NAMES = [
  'infoPanel',
  'notePanel',
  'successPanel',
  'warningPanel',
  'errorPanel',
  'customPanel',
] as const

export type PanelNodeName = (typeof PANEL_NODE_NAMES)[number]

/**
 * Visual identity for the 5 named panels. The custom panel reads its
 * shade + icon from instance attributes; see CUSTOM_PANEL_COLORS /
 * CUSTOM_PANEL_ICONS below.
 *
 * `dataBlockType` is what the renderHTML emits + what `parseHTML`
 * matches against — kept stable + kebab-case so a saved doc keeps
 * parsing if the JS-side name ever shifts.
 */
export interface NamedPanelSpec {
  /** Slash-command discriminator + i18n key suffix. */
  kind: Exclude<PanelKind, 'customPanel'>
  /** ProseMirror node name. */
  nodeName: 'infoPanel' | 'notePanel' | 'successPanel' | 'warningPanel' | 'errorPanel'
  /** `data-block-type` attribute on the rendered HTML — kebab-case. */
  dataBlockType: 'info-panel' | 'note-panel' | 'success-panel' | 'warning-panel' | 'error-panel'
  /**
   * Default color slug used when the node has no `color` attr.
   * Resolves against the unified PANEL_COLOR_CLASSES table; users
   * can override via the in-panel picker (2026-05-29 unification —
   * named panels now accept the same color/icon attrs as customPanel).
   */
  defaultColor: CustomPanelColor
  /** Default icon slug for the same reason — overridable via the picker. */
  defaultIcon: CustomPanelIcon
}

export const NAMED_PANELS: readonly NamedPanelSpec[] = [
  {
    kind: 'info',
    nodeName: 'infoPanel',
    dataBlockType: 'info-panel',
    defaultColor: 'sky',
    defaultIcon: 'info',
  },
  {
    kind: 'note',
    nodeName: 'notePanel',
    dataBlockType: 'note-panel',
    defaultColor: 'violet',
    defaultIcon: 'lightbulb',
  },
  {
    kind: 'success',
    nodeName: 'successPanel',
    dataBlockType: 'success-panel',
    defaultColor: 'emerald',
    defaultIcon: 'circle-check',
  },
  {
    kind: 'warning',
    nodeName: 'warningPanel',
    dataBlockType: 'warning-panel',
    defaultColor: 'amber',
    defaultIcon: 'triangle-alert',
  },
  {
    kind: 'error',
    nodeName: 'errorPanel',
    dataBlockType: 'error-panel',
    defaultColor: 'rose',
    defaultIcon: 'octagon-alert',
  },
]

/**
 * Custom-panel color palette — 8 swatches per the locked decisions. The
 * key (`'sky'` etc.) is what gets serialized to the `data-color`
 * attribute; the rendering side maps it to Tailwind classes via
 * `CUSTOM_PANEL_COLOR_CLASSES` below. Storing the slug (not a hex)
 * keeps the document portable + readable + theme-token-aware.
 */
/**
 * Unified color palette for ALL panels (2026-05-29). Named panels +
 * the explicit `customPanel` all draw from this set via `color` attrs.
 * The "Custom" prefix on the type name is preserved for backwards-
 * compatibility with existing imports; the palette itself covers
 * every panel kind.
 */
export type CustomPanelColor =
  | 'stone'
  | 'slate'
  | 'rose'
  | 'orange'
  | 'amber'
  | 'lime'
  | 'emerald'
  | 'teal'
  | 'sky'
  | 'indigo'
  | 'violet'
  | 'fuchsia'

/**
 * Picker order = perceptual spectrum sweep: neutrals → warm reds → warm
 * yellows → cool greens → cool blues → purples. Gives the picker grid
 * a left-to-right rainbow read instead of an alphabetized jumble.
 */
export const CUSTOM_PANEL_COLORS: readonly CustomPanelColor[] = [
  'stone',
  'slate',
  'rose',
  'orange',
  'amber',
  'lime',
  'emerald',
  'teal',
  'sky',
  'indigo',
  'violet',
  'fuchsia',
]

/**
 * Map of color slug → tailwind utility-class fragment used by the
 * NodeView. Each entry encodes both light + dark variants in the same
 * shape as the 5 named panels (stripe + icon). Hex previews live in
 * the picker swatches — see CUSTOM_PANEL_COLOR_SWATCH_HEX below.
 *
 * Palette trim (2026-05-29 second pass): user reported "some colors
 * are almost indistinguishable" — the prior 18-color set carried 6
 * perceptual near-duplicates at picker swatch size (zinc≈slate,
 * blue≈indigo, purple≈violet, red≈rose, green≈emerald, yellow≈amber).
 * Trimmed to 12 by dropping the redundant cousin in each cluster,
 * keeping the named-panel defaults (sky, violet, emerald, amber, rose)
 * so users can still restore a named panel's hue from the same picker.
 * All retained combinations clear ADR-016 contrast in Default +
 * Obsidian + Midnight Blue. fuchsia/lime/orange still use -600 on the
 * icon disk on light to clear the 3:1 UI floor on white.
 */
export const CUSTOM_PANEL_COLOR_CLASSES: Record<
  CustomPanelColor,
  { panelBgClass: string; iconDiskClass: string }
> = {
  stone: {
    panelBgClass: 'bg-stone-50 dark:bg-stone-500/15',
    iconDiskClass: 'bg-stone-500 dark:bg-stone-400',
  },
  slate: {
    panelBgClass: 'bg-slate-50 dark:bg-slate-500/15',
    iconDiskClass: 'bg-slate-500 dark:bg-slate-400',
  },
  rose: {
    panelBgClass: 'bg-rose-50 dark:bg-rose-500/15',
    iconDiskClass: 'bg-rose-500 dark:bg-rose-400',
  },
  orange: {
    panelBgClass: 'bg-orange-50 dark:bg-orange-500/15',
    iconDiskClass: 'bg-orange-600 dark:bg-orange-400',
  },
  amber: {
    panelBgClass: 'bg-amber-50 dark:bg-amber-500/15',
    iconDiskClass: 'bg-amber-500 dark:bg-amber-400',
  },
  lime: {
    panelBgClass: 'bg-lime-50 dark:bg-lime-500/15',
    iconDiskClass: 'bg-lime-600 dark:bg-lime-400',
  },
  emerald: {
    panelBgClass: 'bg-emerald-50 dark:bg-emerald-500/15',
    iconDiskClass: 'bg-emerald-500 dark:bg-emerald-400',
  },
  teal: {
    panelBgClass: 'bg-teal-50 dark:bg-teal-500/15',
    iconDiskClass: 'bg-teal-500 dark:bg-teal-400',
  },
  sky: {
    panelBgClass: 'bg-sky-50 dark:bg-sky-500/15',
    iconDiskClass: 'bg-sky-500 dark:bg-sky-400',
  },
  indigo: {
    panelBgClass: 'bg-indigo-50 dark:bg-indigo-500/15',
    iconDiskClass: 'bg-indigo-500 dark:bg-indigo-400',
  },
  violet: {
    panelBgClass: 'bg-violet-50 dark:bg-violet-500/15',
    iconDiskClass: 'bg-violet-500 dark:bg-violet-400',
  },
  fuchsia: {
    // -600 disk on light to clear 3:1 against the tinted -50 bg.
    panelBgClass: 'bg-fuchsia-50 dark:bg-fuchsia-500/15',
    iconDiskClass: 'bg-fuchsia-600 dark:bg-fuchsia-400',
  },
}

/**
 * Hex preview for each color — only used by the picker's swatch
 * circles, never by the rendered panel chrome. The hexes correspond
 * to the `stripeClass`'s Tailwind default shade so the swatch the
 * user sees in the picker matches the stripe they get on commit.
 */
export const CUSTOM_PANEL_COLOR_SWATCH_HEX: Record<CustomPanelColor, string> = {
  stone: '#78716c',
  slate: '#64748b',
  rose: '#f43f5e',
  orange: '#ea580c',
  amber: '#f59e0b',
  lime: '#65a30d',
  emerald: '#10b981',
  teal: '#14b8a6',
  sky: '#0ea5e9',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  fuchsia: '#c026d3',
}

/**
 * Custom-panel icon picker. Each icon is keyed by a stable slug stored
 * on the node; the renderer maps slug → Lucide component via this
 * table. Adding a new icon means: append the slug + Lucide ref here,
 * the picker grid surfaces it automatically (the grid iterates the
 * keys), and the renderer resolves it on next render.
 */
export type CustomPanelIcon =
  | 'info'
  | 'lightbulb'
  | 'triangle-alert'
  | 'octagon-alert'
  | 'circle-check'
  | 'pin'
  | 'star'
  | 'lock'
  | 'book-open'
  | 'wrench'
  | 'message-square'
  | 'flag'

export const CUSTOM_PANEL_ICONS: Record<CustomPanelIcon, LucideIcon> = {
  info: Info,
  lightbulb: Lightbulb,
  'triangle-alert': TriangleAlert,
  'octagon-alert': OctagonAlert,
  'circle-check': CheckCircle2,
  pin: Pin,
  star: Star,
  lock: Lock,
  'book-open': BookOpen,
  wrench: Wrench,
  'message-square': MessageSquare,
  flag: Flag,
}

/**
 * Type-guard for caller-supplied color strings (parseHTML / setAttrs).
 * Returns the input narrowed to `CustomPanelColor` when it's a
 * recognized value; the renderer defaults to `slate` for unknown
 * strings so a corrupted document never throws.
 */
export function isCustomPanelColor(value: unknown): value is CustomPanelColor {
  return typeof value === 'string' && (CUSTOM_PANEL_COLORS as readonly string[]).includes(value)
}

/** Same guard for icon slugs. Unknown values fall back to `info`. */
export function isCustomPanelIcon(value: unknown): value is CustomPanelIcon {
  return typeof value === 'string' && value in CUSTOM_PANEL_ICONS
}

/** Defaults for a freshly-inserted custom panel. */
export const DEFAULT_CUSTOM_PANEL_COLOR: CustomPanelColor = 'slate'
export const DEFAULT_CUSTOM_PANEL_ICON: CustomPanelIcon = 'info'

/**
 * Fall-back resolver for icon imports — never throws, always returns a
 * renderable component. The map already covers every well-formed slug,
 * but defensive coding here means a doc authored on a newer version
 * (with a slug not yet shipped to this client) still renders something
 * instead of crashing the NodeView.
 */
export function resolveCustomPanelIcon(slug: string): LucideIcon {
  if (isCustomPanelIcon(slug)) return CUSTOM_PANEL_ICONS[slug]
  return CUSTOM_PANEL_ICONS[DEFAULT_CUSTOM_PANEL_ICON]
}

/** Same fall-back semantics for color. */
export function resolveCustomPanelColorClasses(slug: string): {
  panelBgClass: string
  iconDiskClass: string
} {
  if (isCustomPanelColor(slug)) return CUSTOM_PANEL_COLOR_CLASSES[slug]
  return CUSTOM_PANEL_COLOR_CLASSES[DEFAULT_CUSTOM_PANEL_COLOR]
}

// Re-export the icon set so a caller that only needs the Lucide refs
// (e.g. a future toolbar dropdown) doesn't have to walk the catalog.
export {
  AlertOctagon,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Flag,
  Info,
  Lightbulb,
  Lock,
  MessageSquare,
  OctagonAlert,
  Pin,
  Star,
  TriangleAlert,
  Wrench,
}
